import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Landmark, Plus, Calendar, DollarSign, Clock, Check, AlertTriangle,
  ChevronDown, ChevronRight, FileText, TrendingUp, CreditCard, Download,
  History
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getGuatemalaDate = () => {
  const now = new Date();
  const guatemalaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Guatemala' }));
  return guatemalaTime.toISOString().split('T')[0];
};

const formatCurrency = (value) => {
  return `Q${(value || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Loans = () => {
  const [loans, setLoans] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [expandedLoans, setExpandedLoans] = useState({});
  
  const [formData, setFormData] = useState({
    bank_id: '',
    loan_name: '',
    principal_amount: '',
    interest_rate: '',
    term_months: '',
    start_date: getGuatemalaDate(),
    payment_day_start: '1',
    payment_day_end: '10',
    late_fee_rate: '0.5',
    notes: '',
    is_existing_loan: false,
    installments_already_paid: '0'
  });

  const [confirmData, setConfirmData] = useState({
    payment_date: getGuatemalaDate(),
    notes: ''
  });

  useEffect(() => {
    fetchLoans();
    fetchBanks();
  }, []);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoans(response.data);
    } catch (error) {
      toast.error('Error al cargar préstamos');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data);
    } catch (error) {
      console.error('Error al cargar bancos');
    }
  };

  const fetchLoanDetail = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans/${loanId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLoan(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalle del préstamo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.bank_id || !formData.loan_name || !formData.principal_amount || 
        !formData.interest_rate || !formData.term_months) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/loans`, {
        ...formData,
        principal_amount: parseFloat(formData.principal_amount),
        interest_rate: parseFloat(formData.interest_rate),
        term_months: parseInt(formData.term_months),
        payment_day_start: parseInt(formData.payment_day_start),
        payment_day_end: parseInt(formData.payment_day_end),
        late_fee_rate: parseFloat(formData.late_fee_rate),
        start_date: formData.start_date + 'T00:00:00',
        is_existing_loan: formData.is_existing_loan,
        installments_already_paid: formData.is_existing_loan ? parseInt(formData.installments_already_paid || 0) : 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Préstamo creado exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear préstamo');
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedLoan || !selectedInstallment) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/loans/${selectedLoan.id}/installments/${selectedInstallment.id}/confirm`,
        {
          payment_date: confirmData.payment_date + 'T12:00:00',
          notes: confirmData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.late_fee > 0) {
        toast.success(`Pago confirmado con mora de ${formatCurrency(response.data.late_fee)} (${response.data.days_late} días)`);
      } else {
        toast.success('Pago confirmado exitosamente');
      }
      
      setConfirmDialogOpen(false);
      fetchLoanDetail(selectedLoan.id);
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar pago');
    }
  };

  const resetForm = () => {
    setFormData({
      bank_id: '',
      loan_name: '',
      principal_amount: '',
      interest_rate: '',
      term_months: '',
      start_date: getGuatemalaDate(),
      payment_day_start: '1',
      payment_day_end: '10',
      late_fee_rate: '0.5',
      notes: '',
      is_existing_loan: false,
      installments_already_paid: '0'
    });
  };

  const handleExportPDF = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans/${loanId}/export/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prestamo_${loanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      toast.error('Error al exportar PDF');
    }
  };

  const handleExportExcel = async (loanId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans/${loanId}/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prestamo_${loanId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado exitosamente');
    } catch (error) {
      toast.error('Error al exportar Excel');
    }
  };

  const handleExportAllExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/loans/export/all/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prestamos_resumen.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Resumen de préstamos descargado');
    } catch (error) {
      toast.error('Error al exportar resumen');
    }
  };

  const calculateMonthlyPayment = () => {
    const principal = parseFloat(formData.principal_amount) || 0;
    const rate = parseFloat(formData.interest_rate) || 0;
    const months = parseInt(formData.term_months) || 0;
    
    if (principal <= 0 || months <= 0) return 0;
    
    const monthlyRate = rate / 100 / 12;
    if (monthlyRate === 0) return principal / months;
    
    return principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'ACTIVO': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'PAGADO': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'EN_MORA': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'PENDIENTE': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      'VENCIDO': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'PAGADO_CON_MORA': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };
    
    const labels = {
      'ACTIVO': 'Activo',
      'PAGADO': 'Pagado',
      'EN_MORA': 'En Mora',
      'PENDIENTE': 'Pendiente',
      'VENCIDO': 'Vencido',
      'PAGADO_CON_MORA': 'Pagado con Mora'
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-slate-100 text-slate-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="loans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <CreditCard className="h-8 w-8 text-indigo-600" />
            Préstamos Bancarios
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Gestiona préstamos y cuotas programadas
          </p>
        </div>
        <div className="flex gap-2">
          {loans.length > 0 && (
            <Button variant="outline" onClick={handleExportAllExcel} data-testid="export-all-button">
              <Download className="h-4 w-4 mr-2" />
              Exportar Todo
            </Button>
          )}
          <Button onClick={() => setDialogOpen(true)} data-testid="new-loan-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Préstamo
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Préstamos Activos</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loans.filter(l => l.status === 'ACTIVO').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Cuotas Pendientes</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {loans.reduce((sum, l) => sum + (l.installments_pending || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Cuotas Vencidas</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {loans.reduce((sum, l) => sum + (l.installments_overdue || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <DollarSign className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400">Saldo Total</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatCurrency(loans.reduce((sum, l) => sum + (l.remaining_balance || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Préstamos */}
      {loans.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CreditCard className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
              No hay préstamos registrados
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              Crea tu primer préstamo para comenzar a gestionar las cuotas
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Préstamo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {loans.map(loan => (
            <Card key={loan.id} className="overflow-hidden">
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                onClick={() => setExpandedLoans(prev => ({ ...prev, [loan.id]: !prev[loan.id] }))}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <Landmark className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900 dark:text-white">
                        {loan.loan_name}
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {loan.bank_name} | {loan.term_months} meses | {loan.interest_rate}% anual
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {getStatusBadge(loan.status)}
                        {loan.is_existing_loan && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 flex items-center gap-1">
                            <History className="h-3 w-3" />
                            Préstamo anterior
                          </span>
                        )}
                        {loan.installments_overdue > 0 && (
                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                            {loan.installments_overdue} cuota(s) vencida(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Cuota Mensual</p>
                      <p className="text-lg font-bold text-slate-900 dark:text-white">
                        {formatCurrency(loan.monthly_payment)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Saldo Pendiente</p>
                      <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                        {formatCurrency(loan.remaining_balance)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchLoanDetail(loan.id);
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Ver Cuotas
                      </Button>
                      {expandedLoans[loan.id] 
                        ? <ChevronDown className="h-5 w-5 text-slate-400" />
                        : <ChevronRight className="h-5 w-5 text-slate-400" />
                      }
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Detalle expandido */}
              {expandedLoans[loan.id] && (
                <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Monto Original</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{formatCurrency(loan.principal_amount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Total Pagado</p>
                      <p className="font-semibold text-green-600 dark:text-green-400">{formatCurrency(loan.total_paid)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Fecha Inicio</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{formatDate(loan.start_date)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Fecha Fin</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{formatDate(loan.end_date)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 dark:text-slate-400">Ventana de Pago</p>
                      <p className="font-semibold text-slate-900 dark:text-white">
                        Del {loan.payment_day_start} al {loan.payment_day_end} de cada mes
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Nuevo Préstamo */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-indigo-600" />
              Nuevo Préstamo Bancario
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Banco *</Label>
                <select
                  value={formData.bank_id}
                  onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                  className="w-full p-2 border rounded-md bg-white dark:bg-slate-800 dark:border-slate-600"
                  required
                >
                  <option value="">Seleccionar banco...</option>
                  {banks.map(bank => (
                    <option key={bank.id} value={bank.id}>
                      {bank.bank_name} ({bank.account_number})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Nombre del Préstamo *</Label>
                <Input
                  value={formData.loan_name}
                  onChange={(e) => setFormData({ ...formData, loan_name: e.target.value })}
                  placeholder="Ej: Préstamo Capital de Trabajo"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Monto del Préstamo (Q) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.principal_amount}
                  onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tasa de Interés Anual (%) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                  placeholder="12.00"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Plazo (meses) *</Label>
                <Input
                  type="number"
                  value={formData.term_months}
                  onChange={(e) => setFormData({ ...formData, term_months: e.target.value })}
                  placeholder="12"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fecha de Inicio</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Día Inicio Pago</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.payment_day_start}
                  onChange={(e) => setFormData({ ...formData, payment_day_start: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Día Fin Pago</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.payment_day_end}
                  onChange={(e) => setFormData({ ...formData, payment_day_end: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tasa de Mora Diaria (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.late_fee_rate}
                  onChange={(e) => setFormData({ ...formData, late_fee_rate: e.target.value })}
                  placeholder="0.5"
                />
                <p className="text-xs text-slate-500">Se aplica sobre la cuota por cada día de atraso</p>
              </div>
              
              <div className="space-y-2">
                <Label>Notas</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observaciones opcionales"
                />
              </div>
            </div>

            {/* Sección para préstamos existentes */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="is_existing_loan"
                  checked={formData.is_existing_loan}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    is_existing_loan: e.target.checked,
                    installments_already_paid: e.target.checked ? formData.installments_already_paid : '0'
                  })}
                  className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="is-existing-loan-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="is_existing_loan" className="font-medium text-amber-800 dark:text-amber-300 cursor-pointer">
                    Es un préstamo anterior / existente
                  </label>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Marcar si el préstamo fue adquirido antes de usar este sistema. El dinero NO se sumará al saldo bancario.
                  </p>
                </div>
              </div>
              
              {formData.is_existing_loan && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-amber-800 dark:text-amber-300">Cuotas ya pagadas</Label>
                    <Input
                      type="number"
                      min="0"
                      max={formData.term_months || 999}
                      value={formData.installments_already_paid}
                      onChange={(e) => setFormData({ ...formData, installments_already_paid: e.target.value })}
                      placeholder="0"
                      className="border-amber-300 dark:border-amber-600"
                      data-testid="installments-already-paid-input"
                    />
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Estas cuotas se marcarán automáticamente como "Pagadas"
                    </p>
                  </div>
                  {formData.term_months && parseInt(formData.installments_already_paid || 0) > 0 && (
                    <div className="flex items-center">
                      <div className="p-3 bg-amber-100 dark:bg-amber-800/30 rounded-lg">
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                          <strong>Cuotas restantes:</strong> {parseInt(formData.term_months) - parseInt(formData.installments_already_paid || 0)}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Saldo pendiente aproximado: {formatCurrency(calculateMonthlyPayment() * (parseInt(formData.term_months) - parseInt(formData.installments_already_paid || 0)))}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Preview de Cuota */}
            {formData.principal_amount && formData.term_months && (
              <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                <h4 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2">Cálculo Estimado</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-indigo-600 dark:text-indigo-400">Cuota Mensual</p>
                    <p className="text-xl font-bold text-indigo-900 dark:text-indigo-200">
                      {formatCurrency(calculateMonthlyPayment())}
                    </p>
                  </div>
                  <div>
                    <p className="text-indigo-600 dark:text-indigo-400">Total a Pagar</p>
                    <p className="text-xl font-bold text-indigo-900 dark:text-indigo-200">
                      {formatCurrency(calculateMonthlyPayment() * parseInt(formData.term_months || 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-indigo-600 dark:text-indigo-400">Total Intereses</p>
                    <p className="text-xl font-bold text-indigo-900 dark:text-indigo-200">
                      {formatCurrency((calculateMonthlyPayment() * parseInt(formData.term_months || 0)) - parseFloat(formData.principal_amount || 0))}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1">
                Crear Préstamo
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle de Cuotas */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                {selectedLoan?.loan_name} - Tabla de Amortización
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleExportExcel(selectedLoan?.id)}
                  data-testid="export-excel-button"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Excel
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleExportPDF(selectedLoan?.id)}
                  data-testid="export-pdf-button"
                >
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedLoan && (
            <div className="space-y-4">
              {/* Indicador de préstamo existente */}
              {selectedLoan.is_existing_loan && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700 flex items-center gap-2">
                  <History className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    Este es un préstamo anterior registrado en el sistema. Las cuotas marcadas como pagadas fueron indicadas al momento del registro.
                  </span>
                </div>
              )}
              
              {/* Resumen */}
              <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Monto Original</p>
                  <p className="font-bold text-slate-900 dark:text-white">{formatCurrency(selectedLoan.principal_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Saldo Pendiente</p>
                  <p className="font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(selectedLoan.remaining_balance)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Pagado</p>
                  <p className="font-bold text-green-600 dark:text-green-400">{formatCurrency(selectedLoan.total_paid)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Mora Acumulada</p>
                  <p className="font-bold text-red-600 dark:text-red-400">{formatCurrency(selectedLoan.total_late_fees)}</p>
                </div>
              </div>

              {/* Tabla de Cuotas */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <th className="text-left p-3 font-semibold">No.</th>
                      <th className="text-left p-3 font-semibold">Vencimiento</th>
                      <th className="text-right p-3 font-semibold">Capital</th>
                      <th className="text-right p-3 font-semibold">Interés</th>
                      <th className="text-right p-3 font-semibold">Cuota</th>
                      <th className="text-right p-3 font-semibold">Mora</th>
                      <th className="text-right p-3 font-semibold">Total</th>
                      <th className="text-center p-3 font-semibold">Estado</th>
                      <th className="text-center p-3 font-semibold">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {selectedLoan.installments?.map(inst => (
                      <tr key={inst.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                        inst.status === 'VENCIDO' ? 'bg-red-50 dark:bg-red-900/10' : ''
                      }`}>
                        <td className="p-3 font-medium">{inst.installment_number}</td>
                        <td className="p-3">{formatDate(inst.due_date)}</td>
                        <td className="p-3 text-right font-mono">{formatCurrency(inst.principal_amount)}</td>
                        <td className="p-3 text-right font-mono">{formatCurrency(inst.interest_amount)}</td>
                        <td className="p-3 text-right font-mono font-semibold">{formatCurrency(inst.total_amount)}</td>
                        <td className="p-3 text-right font-mono text-red-600 dark:text-red-400">
                          {inst.late_fee > 0 ? formatCurrency(inst.late_fee) : '-'}
                        </td>
                        <td className="p-3 text-right font-mono font-bold">
                          {formatCurrency(inst.total_with_late_fee)}
                        </td>
                        <td className="p-3 text-center">{getStatusBadge(inst.status)}</td>
                        <td className="p-3 text-center">
                          {(inst.status === 'PENDIENTE' || inst.status === 'VENCIDO') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInstallment(inst);
                                setConfirmData({
                                  payment_date: getGuatemalaDate(),
                                  notes: ''
                                });
                                setConfirmDialogOpen(true);
                              }}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Pagar
                            </Button>
                          )}
                          {inst.status === 'PAGADO' && (
                            <span className="text-green-600 dark:text-green-400">
                              {formatDate(inst.payment_date)}
                            </span>
                          )}
                          {inst.status === 'PAGADO_CON_MORA' && (
                            <span className="text-orange-600 dark:text-orange-400 text-xs">
                              {formatDate(inst.payment_date)}
                              <br />
                              ({inst.days_late} días tarde)
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Pago */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              Confirmar Pago de Cuota
            </DialogTitle>
          </DialogHeader>
          
          {selectedInstallment && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Cuota No.</p>
                    <p className="font-bold">{selectedInstallment.installment_number}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Vencimiento</p>
                    <p className="font-bold">{formatDate(selectedInstallment.due_date)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Monto Cuota</p>
                    <p className="font-bold">{formatCurrency(selectedInstallment.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Mora Acumulada</p>
                    <p className="font-bold text-red-600">{formatCurrency(selectedInstallment.late_fee)}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total a Pagar:</span>
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                      {formatCurrency(selectedInstallment.total_with_late_fee)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Fecha de Pago</Label>
                <Input
                  type="date"
                  value={confirmData.payment_date}
                  onChange={(e) => setConfirmData({ ...confirmData, payment_date: e.target.value })}
                />
                {confirmData.payment_date > selectedInstallment.due_date?.split('T')[0] && (
                  <p className="text-xs text-amber-600">
                    Se calculará mora por los días de atraso
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Input
                  value={confirmData.notes}
                  onChange={(e) => setConfirmData({ ...confirmData, notes: e.target.value })}
                  placeholder="Referencia de pago, etc."
                />
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setConfirmDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button onClick={handleConfirmPayment} className="flex-1 bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4 mr-2" />
                  Confirmar Pago
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Loans;
